import { usePageMeta } from '@/lib/seo';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Lock, Mail, ArrowRight, Globe2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { GoogleSignInButton, AuthDivider } from '@/components/auth/GoogleSignInButton';
import { LegalFooter } from '@/components/common/LegalFooter';
import { InstallPrompt } from '@/components/common/InstallPrompt';
import { extractErrorMessage } from '@/lib/api';

export default function LoginPage() {
  usePageMeta({
    title: 'Log In to RotaHQ | Restaurant Staff Rota Software UK',
    description: 'Log in to RotaHQ account to manage restaurant staff schedules, attendance, payroll, leave & workforce operations from one platform. Start a 14-day free trial.',
    canonicalPath: '/login',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) navigate(user.role === 'super_admin' ? '/super-admin' : '/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('expired') === '1') {
      setError('Your session expired. Please sign in again.');
    }
  }, [location.search]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === 'super_admin' ? '/super-admin' : '/dashboard', { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not sign in.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[color:var(--brand-bg)]">
      <div className="relative hidden lg:flex flex-col justify-between p-10 overflow-hidden">
        <div className="absolute inset-0 hero-decor-gradient" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1620395458832-6436796c2d4c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-[color:var(--brand-bg)]/40" />
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3" aria-label="RotaHQ home" data-testid="login-logo-home-link">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-primary)] text-white shadow-[0_4px_12px_rgba(200,74,47,0.35)]">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold leading-none">RotaHQ</div>
              <div className="text-xs text-muted-foreground">Hospitality scheduling done right</div>
            </div>
          </Link>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative z-10"
        >
          <h1 className="font-display text-4xl xl:text-5xl font-semibold tracking-tight leading-[1.05] text-foreground">
            Scheduling, attendance, <br /> and payroll — made for restaurants.
          </h1>
          <p className="mt-4 text-base text-foreground/80 max-w-md">
            Manage rotas, clock-ins, leave and payroll across every shift, every site. Built like a real SaaS, not a demo.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <span className="pill bg-white/70 border-border text-foreground/80"><span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-success)] inline-block" /> Real-time clock feed</span>
            <span className="pill bg-white/70 border-border text-foreground/80"><span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-accent)] inline-block" /> GPS + IP guarded kiosk</span>
            <span className="pill bg-white/70 border-border text-foreground/80"><span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-primary)] inline-block" /> Multi-tenant ready</span>
          </div>
        </motion.div>
        <div className="relative z-10 flex flex-col gap-1 text-xs text-muted-foreground">
          <span>Trusted by hospitality teams.</span>
          <LegalFooter />
        </div>
      </div>

      <div className="flex items-center justify-center px-5 sm:px-10 py-12">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8" aria-label="RotaHQ home" data-testid="login-logo-home-link-mobile">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-primary)] text-white">
              <Clock className="h-5 w-5" />
            </div>
            <div className="font-display text-lg font-semibold">RotaHQ</div>
          </Link>
          <div className="surface-card p-7">
            <h2 className="font-display text-2xl font-semibold">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your workspace to continue.</p>
            <div className="mt-6">
              <GoogleSignInButton />
              <AuthDivider />
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@restaurant.com"
                    required
                    className="pl-9"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="float-right text-xs text-[color:var(--brand-primary)] hover:underline" data-testid="login-forgot-password-link">
                  Forgot password?
                </Link>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="pl-9"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              {error && (
                <div className="text-sm text-[color:var(--brand-danger)] bg-[color:var(--brand-danger-soft)] border border-[color:var(--brand-danger)]/15 rounded-lg px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full h-11 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)]"
                disabled={submitting}
                data-testid="login-submit-button"
              >
                {submitting ? 'Signing in…' : <span className="inline-flex items-center gap-2">Sign in <ArrowRight className="h-4 w-4" /></span>}
              </Button>
            </form>
            <div className="mt-6 pt-5 border-t border-border space-y-2">
              <div className="text-sm">
                New to RotaHQ?{' '}
                <Link to="/signup" className="text-[color:var(--brand-primary)] font-medium hover:underline" data-testid="login-signup-link">
                  Start your free 14-day trial →
                </Link>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Globe2 className="h-3.5 w-3.5" />
                Employees clock in at the kiosk: <Link to="/kiosk" className="text-[color:var(--brand-primary)] hover:underline" data-testid="login-kiosk-link">find your kiosk →</Link>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Globe2 className="h-3.5 w-3.5" />
                Team members: <Link to="/portal/login" className="text-[color:var(--brand-secondary)] hover:underline" data-testid="login-portal-link">open the team portal →</Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      <InstallPrompt testIdPrefix="login-pwa" />
    </div>
  );
}
